import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Scanner } from '@yudiel/react-qr-scanner';
import DashboardLayout from '../dashboardLayout';
import {
	getActiveQrEventsApi,
	processQrScanApi,
	inviteUserRegistrationApi,
} from '../../../api/adminApi';
import './dynamicQrScanner.css';
import { PulseLoader } from 'react-spinners';
import { errorToast } from '../../../helpers/projectHelper';
import { toast } from 'react-toastify';

const DynamicQrScanner = () => {
	const dispatch = useDispatch();
	const [activeEvents, setActiveEvents] = useState([]);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [showScanner, setShowScanner] = useState(false);
	const [loading, setLoading] = useState(false);
	const [inviteLoading, setInviteLoading] = useState(false);
	const [showInviteForm, setShowInviteForm] = useState(false);
	const [eventsLoading, setEventsLoading] = useState(true);

	// Invite form states
	const [email, setEmail] = useState('');
	const [contactNumber, setContactNumber] = useState('');
	const [whatsappNumber, setWhatsappNumber] = useState('');

	// Scan context states
	const [scanLocation, setScanLocation] = useState('');
	const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
	const [scanNotes, setScanNotes] = useState('');

	useEffect(() => {
		loadActiveEvents();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const loadActiveEvents = async () => {
		try {
			setEventsLoading(true);
			dispatch(getActiveQrEventsApi(setActiveEvents, setEventsLoading));
		} catch (error) {
			errorToast('Failed to load QR events');
		}
	};

	const onScanSuccess = (result) => {
		const rawValue = result[0].rawValue;

		setShowScanner(false);

		const scanData = {
			eventId: selectedEvent._id,
			userId: rawValue,
			timeSlot: selectedTimeSlot || undefined,
			scanLocation: scanLocation || undefined,
			notes: scanNotes || undefined,
		};

		dispatch(
			processQrScanApi(scanData, setLoading, () => {
				// Reset form
				setScanLocation('');
				setSelectedTimeSlot('');
				setScanNotes('');
				setSelectedEvent(null);
			}),
		);
	};

	const onScanFailure = (error) => {
		toast.error(error || 'Failed to scan QR code. Please try again.');
	};

	const handleEventSelect = (event) => {
		setSelectedEvent(event);
		setShowScanner(true);
	};

	const handleInviteUser = () => {
		if (!email && !contactNumber && !whatsappNumber) {
			errorToast('Please enter at least one contact detail.');
			return;
		}

		const userData = {
			email: email || null,
			contactNumber: contactNumber || null,
			whatsappNumber: whatsappNumber || null,
		};

		dispatch(inviteUserRegistrationApi(userData, setInviteLoading));

		setEmail('');
		setContactNumber('');
		setWhatsappNumber('');
		setShowInviteForm(false);
	};

	const renderTimeSlotSelector = () => {
		if (!selectedEvent?.timeSlots || selectedEvent.timeSlots.length === 0) {
			return null;
		}

		return (
			<div className='mb-3'>
				<label className='form-label'>Select Time Slot:</label>
				<select
					className='form-select'
					value={selectedTimeSlot}
					onChange={(e) => setSelectedTimeSlot(e.target.value)}
				>
					<option value=''>-- Select Time Slot --</option>
					{selectedEvent.timeSlots
						.filter((slot) => slot.isActive)
						.map((slot, index) => (
							<option key={index} value={slot.name}>
								{slot.name} ({slot.startTime} - {slot.endTime})
							</option>
						))}
				</select>
			</div>
		);
	};

	return (
		<DashboardLayout>
			{loading ? (
				<div className='loading-table-wrapper'>
					<PulseLoader
						color='#0e5472'
						style={{ display: 'flex', justifyContent: 'center' }}
					/>
				</div>
			) : (
				<div className='container mt-4'>
					<div className='d-flex justify-content-between align-items-center mb-4'>
						<h3>QR Scanner</h3>
						<div>
							<button
								className='btn btn-secondary me-2'
								onClick={() => setShowInviteForm(true)}
							>
								<i className='fas fa-user-plus me-2'></i>
								Invite User
							</button>
							<button
								className='btn btn-primary'
								onClick={loadActiveEvents}
								disabled={eventsLoading}
							>
								<i className='fas fa-sync-alt me-2'></i>
								Refresh Events
							</button>
						</div>
					</div>

					{eventsLoading ? (
						<div className='text-center'>
							<PulseLoader color='#0e5472' />
							<p className='mt-2'>Loading events...</p>
						</div>
					) : (
						<div className='row'>
							{activeEvents.length === 0 ? (
								<div className='col-12'>
									<div className='alert alert-info text-center'>
										<i className='fas fa-info-circle me-2'></i>
										No active QR events found. Contact admin to create events.
									</div>
								</div>
							) : (
								activeEvents.map((event) => (
									<div key={event._id} className='col-md-6 col-lg-4 mb-3'>
										<div className='card'>
											<div className='card-body'>
												<h5 className='card-title'>
													<i
														className={`fas fa-${event.buttonIcon || 'qr-code'
															} me-2`}
													></i>
													{event.displayName}
												</h5>
												{event.description && (
													<p className='card-text text-muted small'>
														{event.description}
													</p>
												)}
												<div className='mb-2'>
													<small className='text-muted'>
														<i className='fas fa-repeat me-1'></i>
														{event.allowMultipleScans
															? `Multiple scans allowed (max: ${event.maxScansPerUser})`
															: 'Single scan only'}
													</small>
												</div>
												{event.timeSlots && event.timeSlots.length > 0 && (
													<div className='mb-2'>
														<small className='text-muted'>
															<i className='fas fa-clock me-1'></i>
															{
																event.timeSlots.filter((slot) => slot.isActive)
																	.length
															}{' '}
															time slots available
														</small>
													</div>
												)}
												{event.totalScans !== undefined && (
													<div className='mb-2'>
														<small className='text-muted'>
															<i className='fas fa-chart-bar me-1'></i>
															Total scans: {event.totalScans}
														</small>
													</div>
												)}
												<button
													className='btn btn-primary w-100'
													style={{ backgroundColor: event.buttonColor }}
													onClick={() => handleEventSelect(event)}
												>
													<i
														className={`fas fa-${event.buttonIcon || 'qr-code'
															} me-2`}
													></i>
													Scan QR Code
												</button>
											</div>
										</div>
									</div>
								))
							)}
						</div>
					)}

					{/* QR Scanner Modal */}
					{showScanner && selectedEvent && (
						<div className='modal show' style={{ display: 'block' }}>
							<div className='modal-dialog modal-md modal-dialog-scrollable'>
								<div className='modal-content'>
									<div className='modal-header'>
										<h5 className='modal-title'>
											<i
												className={`fas fa-${selectedEvent.buttonIcon || 'qr-code'
													} me-2`}
											></i>
											{selectedEvent.displayName}
										</h5>
										<button
											type='button'
											className='btn-close'
											onClick={() => {
												setShowScanner(false);
												setSelectedEvent(null);
												setScanLocation('');
												setSelectedTimeSlot('');
												setScanNotes('');
											}}
										></button>
									</div>
									<div className='modal-body'>
										{selectedEvent.description && (
											<div className='alert alert-info'>
												<i className='fas fa-info-circle me-2'></i>
												{selectedEvent.description}
											</div>
										)}

										{/* Time Slot Selector */}
										{renderTimeSlotSelector()}

										{/* Scan Location */}
										<div className='mb-3'>
											<label className='form-label'>
												Scan Location (optional):
											</label>
											<input
												type='text'
												className='form-control'
												value={scanLocation}
												onChange={(e) => setScanLocation(e.target.value)}
												placeholder='Enter location (e.g., Main Hall, Prayer Area)'
											/>
										</div>

										{/* Notes */}
										<div className='mb-3'>
											<label className='form-label'>Notes (optional):</label>
											<textarea
												className='form-control'
												rows='2'
												value={scanNotes}
												onChange={(e) => setScanNotes(e.target.value)}
												placeholder='Additional notes about this scan'
											/>
										</div>

										{/* QR Scanner */}
										<div className='border p-2'>
											<Scanner
												onScan={onScanSuccess}
												onError={onScanFailure}
												constraints={{
													facingMode: 'environment', // Use rear camera
													aspectRatio: 1, // Square aspect ratio
												}}
											/>
										</div>
									</div>
								</div>
							</div>
							<div
								className='modal-backdrop fade show'
								onClick={() => {
									setShowScanner(false);
									setSelectedEvent(null);
									setScanLocation('');
									setSelectedTimeSlot('');
									setScanNotes('');
								}}
							></div>
						</div>
					)}

					{/* Invite User Modal */}
					{showInviteForm && (
						<div className='modal show' style={{ display: 'block' }}>
							<div className='modal-dialog'>
								<div className='modal-content'>
									<div className='modal-header'>
										<h5 className='modal-title'>
											<i className='fas fa-user-plus me-2'></i>
											Invite User
										</h5>
										<button
											type='button'
											className='btn-close'
											onClick={() => setShowInviteForm(false)}
										></button>
									</div>
									<div className='modal-body'>
										<div className='mb-3'>
											<label className='form-label'>Email:</label>
											<input
												type='email'
												className='form-control'
												value={email}
												onChange={(e) => setEmail(e.target.value)}
												placeholder='Enter email'
											/>
										</div>

										<div className='mb-3'>
											<label className='form-label'>Contact Number:</label>
											<input
												type='text'
												className='form-control'
												value={contactNumber}
												onChange={(e) => setContactNumber(e.target.value)}
												placeholder='Enter contact number'
											/>
										</div>

										<div className='mb-3'>
											<label className='form-label'>WhatsApp Number:</label>
											<input
												type='text'
												className='form-control'
												value={whatsappNumber}
												onChange={(e) => setWhatsappNumber(e.target.value)}
												placeholder='Enter WhatsApp number'
											/>
										</div>
									</div>
									<div className='modal-footer'>
										<button
											className='btn btn-success'
											onClick={handleInviteUser}
											disabled={inviteLoading}
										>
											{inviteLoading ? (
												<>
													<PulseLoader size={6} color='#fff' className='me-2' />
													Sending...
												</>
											) : (
												<>
													<i className='fas fa-paper-plane me-2'></i>
													Send Invite
												</>
											)}
										</button>
										<button
											className='btn btn-secondary'
											onClick={() => setShowInviteForm(false)}
										>
											Close
										</button>
									</div>
								</div>
							</div>
							<div
								className='modal-backdrop fade show'
								onClick={() => setShowInviteForm(false)}
							></div>
						</div>
					)}
				</div>
			)}
		</DashboardLayout>
	);
};

export default DynamicQrScanner;
